import React, { useRef, useState } from "react";
import * as PR from "../prime-modules/index";
import * as utils from "../utils";

const checkIsSameCurrency = (row) => {
    if (!row) return true;
    if (row.originalCurrency && row.priceCurrency) {
        return row.originalCurrency === row.priceCurrency;
    }
    if (row.totalValueInBase !== undefined && row.totalValueInBase !== null && row.totalOrderAmount !== undefined && row.totalOrderAmount !== null) {
        return row.totalValueInBase === row.totalOrderAmount;
    }
    return true;
};

const PriceDisplay = ({ rowData }) => {
    const priceOp = useRef(null);
    const [hoveredPriceRow, setHoveredPriceRow] = useState(null);

    const isSameCurrency = checkIsSameCurrency(rowData);

    const hasTooltip = () => {
        const hasDiscount = rowData?.priceBeforeDiscount > 0 || rowData?.priceBeforeDiscountInBase > 0 || (rowData?.dataPlans?.[0]?.priceBeforeDiscount > 0);
        const hasDifferentCurrencies = !isSameCurrency && (rowData?.totalValueInBase !== undefined && rowData?.totalValueInBase !== null) && (rowData?.totalOrderAmount !== undefined && rowData?.totalOrderAmount !== null);
        return hasDiscount || hasDifferentCurrencies;
    };
    
    const handlePriceMouseEnter = (event) => {
        if (priceOp.current && hasTooltip()) {
            priceOp.current.show(event);
            setHoveredPriceRow(rowData);
        }
    };

    const handlePriceMouseLeave = () => {
        if (priceOp.current) {
            priceOp.current.hide();
            setHoveredPriceRow(null);
        }
    };

    const renderPriceContent = () => {
        if (!hoveredPriceRow) return null;
        const row = hoveredPriceRow;
        const isSameCurr = checkIsSameCurrency(row);
        const baseCurrency = row?.originalCurrency || 'USD';
        const paidCurrency = row?.priceCurrency;

        return (
            <div style={{ marginBottom: '0.05rem', color: "#fff", fontSize: "13px", minWidth: '200px' }}>
                <p style={{ borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '6px', marginBottom: '8px' }}>
                    <strong >Price Details</strong>
                </p>
                {/* Base Price Section */}
                {(row?.priceBeforeDiscountInBase !== undefined && row?.priceBeforeDiscountInBase !== null) || (row?.totalValueInBase !== undefined && row?.totalValueInBase !== null) ? (
                    <div className="mb-2">
                        <p><strong>Base Price:</strong> {utils.formatPriceCurrency(baseCurrency, row?.priceBeforeDiscountInBase ?? row?.totalValueInBase)}</p>
                        {row?.priceBeforeDiscountInBase > 0 && row?.priceBeforeDiscountInBase !== row?.totalValueInBase && (
                            <p><strong>Discount Price:</strong> {utils.formatPriceCurrency(baseCurrency, row?.totalValueInBase)}</p>
                        )}
                    </div>
                ) : null}

                {/* Paid Price Section — only when currencies differ */}
                {!isSameCurr && (row?.totalOrderAmount !== undefined && row?.totalOrderAmount !== null) && (
                    <div className="mb-2" style={{ borderTop: (row?.priceBeforeDiscountInBase !== undefined || row?.totalValueInBase !== undefined) ? '1px dashed rgba(255,255,255,0.1)' : 'none', paddingTop: (row?.priceBeforeDiscountInBase !== undefined || row?.totalValueInBase !== undefined) ? '8px' : '0' }}>
                        <p><strong>Paid Price:</strong> {utils.formatPriceCurrency(paidCurrency, row?.totalOrderAmount)}</p>
                        
                    </div>
                )}

                {/* Exchange Rate — only when currencies differ */}
                {!isSameCurr && row?.exchangeRate && (
                    <p className="mt-2 pt-2" style={{ borderTop: '1px dashed rgba(255,255,255,0.1)' }}>
                        <strong>Exchange Rate:</strong> {row?.exchangeRate}
                    </p>
                )}
            </div>
        );
    };

    // Check if it's a subscription order from OrderDetails context
    const isSubscription = rowData?.dataPlans?.[0]?.isSubscription;
    const subscriptionPrice = rowData?.dataPlans?.[0]?.price;

    return (
        <div 
            className="price-display-wrapper"
            onMouseEnter={handlePriceMouseEnter}
            onMouseLeave={handlePriceMouseLeave}
            style={{ cursor: 'pointer', display: 'inline-block' }}
        >
            {isSubscription ? (
                <div className="price-base-row">
                    {(subscriptionPrice !== undefined && subscriptionPrice !== null) ? utils.formatPriceCurrency(rowData?.priceCurrency, subscriptionPrice) : '-'}
                </div>
            ) : isSameCurrency ? (
                <div className="price-base-row">
                    {rowData.priceBeforeDiscount > 0 && rowData.priceBeforeDiscount !== rowData.totalOrderAmount ? (
                        <>
                            <span className="red-color strike-through">
                                {utils.formatPriceCurrency(rowData?.originalCurrency || rowData?.priceCurrency, rowData.priceBeforeDiscount)}
                            </span>
                            {' '}
                            <span>
                                {utils.formatPriceCurrency(rowData?.originalCurrency || rowData?.priceCurrency, rowData.totalOrderAmount)}
                            </span>
                        </>
                    ) : (
                        <span>
                            {(rowData.totalOrderAmount !== undefined && rowData.totalOrderAmount !== null) ? utils.formatPriceCurrency(rowData?.originalCurrency || rowData?.priceCurrency, rowData.totalOrderAmount) : '-'}
                        </span>
                    )}
                </div>
            ) : (
                <>
                    {(rowData.totalValueInBase !== undefined && rowData.totalValueInBase !== null) && (
                        <div className="price-base-row">
                            {rowData.priceBeforeDiscountInBase > 0 && rowData.priceBeforeDiscountInBase !== rowData.totalValueInBase ? (
                                <>
                                    <span className="red-color strike-through">
                                        {utils.formatPriceCurrency(rowData?.originalCurrency || 'USD', rowData.priceBeforeDiscountInBase)}
                                    </span>
                                    {' '}
                                    <span>
                                        {utils.formatPriceCurrency(rowData?.originalCurrency || 'USD', rowData.totalValueInBase)}
                                    </span>
                                </>
                            ) : (
                                <span>
                                    {utils.formatPriceCurrency(rowData?.originalCurrency || 'USD', rowData.totalValueInBase)}
                                </span>
                            )}
                        </div>
                    )}
                    {(rowData.totalOrderAmount !== undefined && rowData.totalOrderAmount !== null) && (
                        <div className="price-conversion-row">
                            <span>
                                {utils.formatPriceCurrency(rowData?.priceCurrency, rowData.totalOrderAmount)}
                            </span>
                        </div>
                    )}
                </>
            )}
            <PR.OverlayPanel
                ref={priceOp}
                dismissable
                style={{
                    backgroundColor: "#3a4668",
                    borderRadius: "8px",
                }}
                className="promoCode-tooltip-new"
            >
                {renderPriceContent()}
            </PR.OverlayPanel>
        </div>
    );
};

export default PriceDisplay;
